import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {Box, getValueByDevice, useDevice, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, defaultTo} from 'ramda';

import {useGetCurrentUserInfoQuery, useGetUserDashboardSettingsQuery} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useIsLicenceEnabled, usePermissions} from '@dms/shared';

import {ClockRow} from './components/ClockRow';
import {DashboardGrid} from './components/DashboardGrid/DashboardGrid';
import {DashboardHiddenTiles} from './components/DashboardHiddenTiles/DashboardHiddenTiles';
import {DashboardLayoutActions} from './components/DashboardLayoutActions';
import {LatestBusinessCases} from './components/LatestBusinessCases/LatestBusinessCases';
import {LatestInterests} from './components/LatestInterests/LatestInterests';
import {LatestVehicles} from './components/LatestVehicles/LatestVehicles';
import {MyNotifications} from './components/MyNotifications';
import {MyTasks} from './components/MyTasks';
import {OpportunitiesFunnel} from './components/OpportunitiesFunnel';
import {RecommendedActions} from './components/RecommendedActions';
import {Support} from './components/Support';
import {TasksAndNotifications} from './components/TasksAndNotifications';
import {VehicleWarehouse} from './components/VehicleWarehouse/VehicleWarehouse';
import {hasEnabledWorkshopTiles} from './components/WorkshopOverview/utils/hasEnabledWorkshopTiles';
import {WorkshopOverview} from './components/WorkshopOverview/WorkshopOverview';
import {useDashboardLayout} from './hooks/useDashboardLayout';
import {DashboardSource} from './types/DashboardSource';
import {DashboardTileDefinition} from './types/DashboardTileDefinition';
import {createDashboardTileRenderer} from './utils/createDashboardTileRenderer';

const defaultToFalse = defaultTo(false);
const MINIMUM_ARRANGEABLE_TILES = 2;

export function Dashboard() {
  const [
    canReadInterest,
    canReadBusinessCase,
    canViewBusinessCasePurchase,
    canViewBusinessCaseSelling,
    canReadVehicle,
    canReadTasks,
  ] = usePermissions({
    permissionKeys: [
      'interestRead',
      'businessCaseRead',
      'viewBusinessCasePurchase',
      'viewBusinessCaseSelling',
      'vehicleRead',
      'readTask',
    ],
    scopes: {
      interestRead: {
        // EP for latest interests handles participation scope
        participation: 'SKIP',
        // EP for latest interests handles branch scope
        branchId: 'SKIP',
      },
      viewBusinessCasePurchase: {
        // EP for latest BCs handles participation scope
        participation: 'SKIP',
        // EP for latest BCs handles branch scope
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // EP for latest BCs handles participation scope
        participation: 'SKIP',
        // EP for latest BCs handles branch scope
        branchId: 'SKIP',
      },
    },
  });

  const [dashboardSource, setDashboardSource] = useState<DashboardSource>(
    DashboardSource.activeBranch
  );
  const device = useDevice();

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();

  const {data: userInfo} = useGetCurrentUserInfoQuery();
  const {data: userDashboardSettings, isLoading: isUserDashboardSettingsLoading} =
    useGetUserDashboardSettingsQuery();

  const selectedBranchId = match(dashboardSource)
    .with(
      DashboardSource.activeBranch,
      // There is edge case, when user is new and has no branch set, you have to check defaultBranch
      () => userInfo?.settings?.branch || userInfo?.defaultBranch?.id
    )
    .otherwise(always(undefined));

  const branchName = userInfo?.branches?.find(
    (branch) => branch.id === userInfo?.settings?.branch
  )?.name;

  // With customisation on, the arrangement decides whether the funnel shows, and a shown funnel
  // carries both of its halves.
  const isCustomisationEnabled = isFeatureEnabled(featureFlags.DASHBOARD_CUSTOMISATION);

  const isFunnelInterestsEnabled = defaultToFalse(userDashboardSettings?.salesFunnelInterests);
  const isFunnelBusinessCasesEnabled = defaultToFalse(
    userDashboardSettings?.salesFunnelBusinessCases
  );

  const canViewLatestBusinessCase = canViewBusinessCasePurchase || canViewBusinessCaseSelling;

  const tileDefinitions: DashboardTileDefinition[] = [
    {
      id: 'opportunities-funnel',
      renderer: createDashboardTileRenderer(OpportunitiesFunnel, {
        branchId: selectedBranchId,
        isFunnelInterestsEnabled: isCustomisationEnabled || isFunnelInterestsEnabled,
        isFunnelBusinessCasesEnabled: isCustomisationEnabled || isFunnelBusinessCasesEnabled,
      }),
      isAllowed: canReadBusinessCase,
      isEnabled: isFunnelInterestsEnabled || isFunnelBusinessCasesEnabled,
    },
    {
      id: 'workshop-overview',
      renderer: createDashboardTileRenderer(WorkshopOverview, {}),
      isAllowed: isWorkshopModuleEnabled,
      isEnabled: hasEnabledWorkshopTiles(userDashboardSettings),
    },
    {
      id: 'vehicle-warehouse',
      renderer: createDashboardTileRenderer(VehicleWarehouse, {
        isActiveBranch: dashboardSource === DashboardSource.activeBranch,
        branchId: selectedBranchId,
        canReadVehicle,
      }),
      isAllowed: canReadBusinessCase,
      isEnabled: defaultToFalse(userDashboardSettings?.vehicleStock),
    },
    {
      id: 'support',
      renderer: createDashboardTileRenderer(Support, {}),
      isAllowed: true,
      isEnabled: true,
    },
    {
      id: 'latest-vehicles',
      renderer: createDashboardTileRenderer(LatestVehicles, {branchId: selectedBranchId}),
      isAllowed: true,
      isEnabled: defaultToFalse(userDashboardSettings?.latestVehicles),
    },
    // Without customisation the `my-tasks` slot keeps the combined tile users know: it shows whenever
    // either part is on and takes the whole half, as `notifications` is not allowed beside it.
    isCustomisationEnabled
      ? {
          id: 'my-tasks',
          renderer: createDashboardTileRenderer(MyTasks, {}),
          isAllowed: canReadTasks,
          isEnabled: defaultToFalse(userDashboardSettings?.myTasks),
        }
      : {
          id: 'my-tasks',
          renderer: createDashboardTileRenderer(TasksAndNotifications, {}),
          isAllowed: true,
          isEnabled: defaultToFalse(
            userDashboardSettings?.notifications || userDashboardSettings?.myTasks
          ),
        },
    {
      id: 'notifications',
      renderer: createDashboardTileRenderer(MyNotifications, {}),
      isAllowed: isCustomisationEnabled,
      isEnabled: defaultToFalse(userDashboardSettings?.notifications),
    },
    {
      id: 'latest-business-cases',
      renderer: createDashboardTileRenderer(LatestBusinessCases, {branchId: selectedBranchId}),
      isAllowed: canViewLatestBusinessCase && canReadBusinessCase,
      isEnabled: defaultToFalse(userDashboardSettings?.latestBusinessCases),
    },
    {
      id: 'latest-interests',
      renderer: createDashboardTileRenderer(LatestInterests, {branchId: selectedBranchId}),
      isAllowed: canReadInterest && canReadBusinessCase,
      isEnabled: defaultToFalse(userDashboardSettings?.latestInterests),
    },
    {
      id: 'recommended-actions',
      renderer: createDashboardTileRenderer(RecommendedActions, {}),
      isAllowed: canReadInterest && isFeatureEnabled(featureFlags.INTEREST_RECOMMENDATIONS),
      isEnabled: true,
    },
  ];

  const layout = useDashboardLayout({definitions: tileDefinitions});

  // A narrowed window only hides customise mode; the draft itself survives.
  const isAuthoringAvailable = getValueByDevice(device, false, true, true, true);
  const isCustomising = layout.isCustomising && isAuthoringAvailable;

  // Customising again while the previous save is in flight would let an older arrangement land last.
  const isCustomisationAvailable =
    !isUserDashboardSettingsLoading &&
    !layout.isSaving &&
    layout.allowedTileCount >= MINIMUM_ARRANGEABLE_TILES;

  return (
    <>
      <Helmet>
        <title>{i18n.t('page.homepage.labels.title')}</title>
      </Helmet>
      <Box padding={[2, 4]} data-testid={testIds.dashboard.home('wrapper')}>
        <VStack spacing={[3, 4]}>
          <ClockRow
            dashboardSource={dashboardSource}
            setDashboardSource={setDashboardSource}
            branchName={branchName}
            actions={
              <DashboardLayoutActions
                isCustomising={isCustomising}
                isCustomisationAvailable={isCustomisationAvailable}
                onStart={layout.startCustomising}
                onSave={layout.saveArrangement}
                onCancel={layout.cancelCustomising}
                onReset={layout.resetArrangement}
                data-testid={testIds.dashboard.home('layoutActions')}
              />
            }
          />

          <DashboardHiddenTiles
            tiles={layout.hiddenTiles}
            isCustomising={isCustomising}
            onShow={layout.showTile}
            data-testid={testIds.dashboard.home('dashboard')}
          />

          <DataStatus minHeight={80} isLoading={isUserDashboardSettingsLoading}>
            <DashboardGrid
              tiles={layout.shownTiles}
              isCustomising={isCustomising}
              onDrop={layout.applyDrop}
              onResize={layout.resizeTile}
              onHide={layout.hideTile}
              data-testid={testIds.dashboard.home('dashboard')}
            />
          </DataStatus>
        </VStack>
      </Box>
    </>
  );
}
